import React, { useEffect, useState, useCallback } from "react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileExport, faSync, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import API_BASE_URL from "../../config";
import './OltLog.css';

const PAGE_SIZE = 50; 

const OLTLog = ({ onBack }) => {
    const [logs, setLogs] = useState([]);
    const [oltList, setOltList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [selectedOlt, setSelectedOlt] = useState("all");
    const [hasMore, setHasMore] = useState(true);

    // Fetch OLT List
    useEffect(() => {
        const fetchOltList = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/device-manage/list/olt`);
                const resData = await response.json();
                
                let rawList = [];
                if (resData && Array.isArray(resData.data)) {
                    rawList = resData.data;
                } else if (Array.isArray(resData)) {
                    rawList = resData;
                }

                if (rawList.length > 0) {
                    const sorted = rawList
                        .map(d => d.hostname)
                        .filter(Boolean)
                        .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
                    
                    setOltList(sorted);
                }
            } catch (err) { 
                console.error("List Fetch Error:", err); 
            }
        };
        fetchOltList();
    }, []);

    // Fetch Initial Logs
    const fetchLogs = useCallback(async () => {
        if (selectedOlt === "all") {
            setLogs([]);
            setHasMore(false);
            return;
        }

        setLoading(true);
        try {
            const url = `${API_BASE_URL}/log-monitor/olt/${selectedOlt}?limit=${PAGE_SIZE}&offset=0`;
            const response = await fetch(url);
            const data = await response.json();
            
            const validData = Array.isArray(data) ? data : [];
            setLogs(validData);
            setHasMore(validData.length >= PAGE_SIZE); 
        } catch (err) { 
            console.error("Logs Fetch Error:", err);
            setLogs([]); 
            setHasMore(false);
        } finally { 
            setLoading(false); 
        }
    }, [selectedOlt]);

    // Load More Function
    const loadMoreLogs = async () => {
        if (selectedOlt === "all" || loadingMore) return;

        setLoadingMore(true);
        const currentOffset = logs.length;

        try {
            const url = `${API_BASE_URL}/log-monitor/olt/${selectedOlt}?limit=${PAGE_SIZE}&offset=${currentOffset}`;
            const response = await fetch(url);
            const newLogs = await response.json();

            if (Array.isArray(newLogs) && newLogs.length > 0) {
                setLogs(prev => [...prev, ...newLogs]);
                setHasMore(newLogs.length >= PAGE_SIZE);
            } else {
                setHasMore(false);
            }
        } catch (err) {
            console.error("Load More Logs Error:", err);
        } finally {
            setLoadingMore(false);
        }
    };

    useEffect(() => {
        fetchLogs();
        const interval = setInterval(fetchLogs, 15000);
        return () => clearInterval(interval);
    }, [fetchLogs]);

    const exportToCSV = () => {
        if (logs.length === 0) return;
        const headers = ["HOSTNAME", "DATE", "TIME", "INTERFACE/ONU", "LOG MESSAGE\n"];
        const rows = logs.map(log => {
            const timeParts = log.time ? log.time.split(" ") : ["", ""];
            const logDate = timeParts[0] || "";
            const logTime = timeParts[1] || "";
            const interfaceOnu = `${log.pon_port || 'OLT'}${log.onu_id ? ':' + log.onu_id : ''}`;
            const cleanMsg = log.message ? log.message.replace(/"/g, '""') : "";

            return `"${log.hostname || ''}","${logDate}","${logTime}","${interfaceOnu}","${cleanMsg}"`;
        });
        const blob = new Blob([headers.join(",") + rows.join("\n")], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.setAttribute("download", `OLT_${selectedOlt}_LOGS.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className="olt-log-main">
            {/* Header Area */}
            <div className="olt-log-header">
                <button className="back-button" onClick={onBack}>
                    <FontAwesomeIcon icon={faArrowLeft} /> Back
                </button>

                <h2 className="title-text">OLT LIVE LOGS</h2>

                <div className="header-controls">
                    <select className="olt-select" value={selectedOlt} onChange={(e) => {
                        setSelectedOlt(e.target.value);
                    }}>
                        <option value="all">SELECT OLT</option>
                        {oltList.map(name => <option key={name} value={name}>{name.toUpperCase()}</option>)}
                    </select>

                    <button className="action-btn refresh" title="Refresh" onClick={fetchLogs} disabled={loading || selectedOlt === "all"}>
                        <FontAwesomeIcon icon={faSync} spin={loading} />
                    </button>

                    <button className="action-btn export" title="Export CSV" onClick={exportToCSV} disabled={logs.length === 0}>
                        <FontAwesomeIcon icon={faFileExport} />
                    </button>
                </div>
            </div>

            {/* Table Area */}
            <div className="log-container">
                <div className="log-table-header">
                    <div className="h-col host">HOSTNAME</div>
                    <div className="h-col time">TIMESTAMP</div>
                    <div className="h-col detail">INTERFACE/ONU</div>
                    <div className="h-col msg">LOG MESSAGE</div>
                </div>
                
                <div className="log-rows-wrapper">
                    {logs.map((log, index) => {
                        const timeParts = log.time ? log.time.split(" ") : ["", ""];
                        const displayDate = timeParts[0] || "";
                        const displayTime = timeParts[1] || "";

                        let statusClass = "info";
                        if (log.status_type === "down") statusClass = "danger";
                        if (log.status_type === "up") statusClass = "success";
                        if (log.status_type === "warning") statusClass = "warning";

                        return (
                            <div key={log.id || index} className={`log-row status-${statusClass}`}>
                                <div className="b-col host">{log.hostname}</div>
                                <div className="b-col time">
                                    <small className="log-date-label">{displayDate}</small>
                                    <div className="log-time-label">{displayTime}</div>
                                </div>
                                <div className="b-col detail">
                                    {log.pon_port ? log.pon_port : "OLT"}
                                    {log.onu_id && <span className="onu-tag">:{log.onu_id}</span>}
                                </div>
                                <div className="b-col msg">{log.message}</div>
                            </div>
                        );
                    })}
                    
                    {logs.length === 0 && !loading && (
                        <div className="no-data">
                            {selectedOlt === "all" ? "Please select an OLT to load logs." : "No logs found."}
                        </div>
                    )}
                </div>
            </div>

            {/* Guaranteed Load More Button (Forced Inline Visibility) */}
            {selectedOlt !== "all" && logs.length > 0 && (
                <div style={{ textAlign: 'center', padding: '15px 0', background: '#1e1e2f', marginTop: '10px', borderRadius: '6px' }}>
                    {hasMore ? (
                        <button 
                            onClick={loadMoreLogs}
                            disabled={loadingMore}
                            style={{
                                padding: '10px 24px',
                                backgroundColor: '#007bff',
                                color: '#ffffff',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontWeight: 'bold',
                                fontSize: '14px',
                                boxShadow: '0 2px 5px rgba(0,0,0,0.3)'
                            }}
                        >
                            {loadingMore ? "Loading..." : "Load More Logs (+50)"}
                        </button>
                    ) : (
                        <span style={{ color: '#888', fontSize: '13px' }}>— All logs loaded —</span>
                    )}
                </div>
            )}
        </div>
    );
};

export default OLTLog;